import { useEffect, useState } from 'react';
import { Shield } from 'lucide-react';
import { get } from '../../../../api/client';

// 条款配置（与后端 system-config terms namespace 对齐）
interface TermsConfig {
  title: string;
  content: string;
  countdownSeconds: number;
  version: string;
}

// /public/config 响应结构（具名 interface 避免泛型内联类型触发 hooks 依赖误报）
interface PublicConfigResponse {
  terms?: Partial<TermsConfig>;
}

// 硬编码兜底：后端未配置 terms 时使用（与原 Step1 内容一致）
const FALLBACK_TERMS: TermsConfig = {
  title: '平台使用规则',
  countdownSeconds: 30,
  version: '1.0',
  content: `1、注册前，请您务必完全了解本系统的作用和功能，确定是否符合您的使用要求；需要使用你的个人信息，请谨慎使用。
2、注意：本系统只提供订单添加、查询、对账抵消等辅助功能，不介入订单纠纷、不参与订单价格制定和约束、订单的添加和取消均来自于您和您的客户，订单添加错误、取消、违约等各种因素造成的损失，我们不承担任何责任。
3、根据订单系统设定规则，注册用户必须使用用户本人的真实信息进行注册；不能完全保证个人信息的绝对安全，确定是否愿意接受及使用。
4、注册时，必须正确填写您的真实身份信息；这是确保您的身份在系统中唯一的凭证，也是确保您的订单能够正确关联的保障。
5、为保障您和其他用户的身份准确和真实，我们在身份验证过程中将核验你的身份信息真伪；对虚假的注册用户将清空您的订单和用户信息且不承担任何责任。
6、我们将遵守《个人信息保护法》的要求来保护您的敏感信息。用户的身份证信息使用目的和范围：仅用于系统数据间的核验和比对，确保用户身份的唯一性。
7、系统采用"首填机制"进行客户信息的添加，后续用户在添加客户时，如果客户存在将进行关联添加，保证系统客户用户身份唯一。
8、添加客户时，请先和客户取得联系，确认常用联系方式是个不错的选择。
9、系统采用"首填机制"，当您需要注册成为用户时，系统检测到您的个人信息存在时，请不要惊讶；这是因为有用户添加您为客户所致，不会影响您的用户注册。
10、当用户注销时，存在交互的订单信息不会跟随注销；本人孤立订单（仅本人提交、且对方不是注册用户的订单）随注销同步删除。注销时若本人添加的客户在系统内已无其他用户绑定，该孤立客户一并删除。
11、注意：本系统不参与订单费用的清算转账内容，所有问题请订单关联双方沟通解决。
12、系统只是给您提供了一个方便的、能实时添加和查看个人订单的网络信息平台，希望您在使用便捷的同时，也不要完全依赖本系统。
13、网络方便，但不能保证绝对安全，请谨慎使用！感谢您的到来!`,
};

// Step 1: 注册必读（条款由后台配置下发，未配置时使用内置兜底）
export function Step1RegisterGuide({
  agreed,
  setAgreed,
  hasReadToBottom,
  setHasReadToBottom,
  countdown,
}: {
  agreed: boolean;
  setAgreed: (value: boolean) => void;
  hasReadToBottom: boolean;
  setHasReadToBottom: (value: boolean) => void;
  countdown: number;
}) {
  const [terms, setTerms] = useState<TermsConfig>(FALLBACK_TERMS);

  useEffect(() => {
    let cancelled = false;
    get<PublicConfigResponse>('/public/config')
      .then((config) => {
        if (cancelled || !config?.terms?.content) return;
        setTerms({
          title: config.terms.title || FALLBACK_TERMS.title,
          content: config.terms.content,
          countdownSeconds: config.terms.countdownSeconds || FALLBACK_TERMS.countdownSeconds,
          version: config.terms.version || FALLBACK_TERMS.version,
        });
      })
      .catch(() => {
        // 拉取失败静默使用兜底条款
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-4">
      {countdown > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-500" />
          <p className="text-sm text-amber-700">
            请仔细阅读，关系到您是否适合使用本平台
          </p>
        </div>
      )}

      <div
        onScroll={(e) => {
          const target = e.target as HTMLElement;
          if (target.scrollHeight - target.scrollTop - target.clientHeight < 50) {
            setHasReadToBottom(true);
          }
        }}
        className="border border-slate-200 rounded-xl p-4 max-h-64 overflow-y-auto text-sm text-slate-600 bg-slate-50 leading-relaxed whitespace-pre-wrap"
      >
        <h5 className="font-medium text-slate-800 mb-3">
          {terms.title}
          {terms.version && terms.version !== '1.0' && (
            <span className="ml-2 text-xs text-slate-400">v{terms.version}</span>
          )}
        </h5>
        {terms.content}

        <div className="mt-6 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={hasReadToBottom}
              disabled
              className="w-4 h-4 text-teal-500 rounded border-slate-300"
            />
            <span className={`text-sm ${hasReadToBottom ? 'text-green-600' : 'text-slate-400'}`}>
              {hasReadToBottom ? '✓ 已阅读到内容末尾' : '请继续阅读...'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="agree-terms"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-teal-500 rounded border-slate-300"
        />
        <label htmlFor="agree-terms" className="text-sm text-slate-600 cursor-pointer hover:text-slate-800 transition-colors">
          我已阅读并同意 <span className="text-teal-600 font-medium">《服务条款》</span> 和 <span className="text-teal-600 font-medium">《隐私政策》</span>，了解平台使用规则
        </label>
      </div>
    </div>
  );
}